import { sanitizeMediaUrl } from '@/lib/urlSafety'

/**
 * Returns true when running inside an iframe (e.g. Canvas LTI embedding).
 * Uses a try/catch because accessing window.top throws for cross-origin frames.
 */
function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true // cross-origin access blocked → definitely in an iframe
  }
}

/**
 * Triggers a file download, handling sandboxed iframes (Canvas LTI).
 *
 * Browsers block <a download>.click() inside sandboxed iframes that lack the
 * `allow-downloads` permission.  Canvas grants `allow-popups` but not
 * `allow-downloads`, so we fall back to window.open() which opens the URL
 * in a new tab and lets the browser's native save dialog take over.
 *
 * @param url      The URL or blob: URL to download.
 * @param filename Suggested filename (only used for the anchor path).
 * @param revoke   Whether to revoke a blob: URL after use.
 */
export function triggerDownload(url: string, filename: string, revoke = false, openInNewTab = false): void {
  if (isInIframe() || openInNewTab) {
    window.open(url, '_blank')
    if (revoke) setTimeout(() => URL.revokeObjectURL(url), 30_000)
    return
  }
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  if (revoke) URL.revokeObjectURL(url)
}

const COPY_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`

const CHECK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`

const DOWNLOAD_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>`

export function createCopyButton(getText: () => string): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.className = 'copy-btn'
  btn.innerHTML = COPY_ICON
  btn.title = 'Copy'

  btn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(getText())
      btn.innerHTML = CHECK_ICON
      btn.classList.add('copied')
      setTimeout(() => {
        btn.innerHTML = COPY_ICON
        btn.classList.remove('copied')
      }, 2000)
    } catch {
      // Clipboard write failed
    }
  })

  return btn
}

export function createUrlDownloadButton(url: string, filename: string, openInNewTab = false): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.className = 'copy-btn'
  btn.innerHTML = DOWNLOAD_ICON
  btn.title = `Download ${filename}`

  btn.addEventListener('click', () => {
    const safeUrl = sanitizeMediaUrl(url)
    if (!safeUrl) return
    triggerDownload(safeUrl, filename, false, openInNewTab)
    btn.innerHTML = CHECK_ICON
    btn.classList.add('copied')
    setTimeout(() => {
      btn.innerHTML = DOWNLOAD_ICON
      btn.classList.remove('copied')
    }, 2000)
  })

  return btn
}

export function createDownloadButton(getBlob: () => Blob, filename: string): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.className = 'copy-btn'
  btn.innerHTML = DOWNLOAD_ICON
  btn.title = `Download ${filename}`

  btn.addEventListener('click', () => {
    const blob = getBlob()
    const url = URL.createObjectURL(blob)
    triggerDownload(url, filename, true)

    btn.innerHTML = CHECK_ICON
    btn.classList.add('copied')
    setTimeout(() => {
      btn.innerHTML = DOWNLOAD_ICON
      btn.classList.remove('copied')
    }, 2000)
  })

  return btn
}

export function addCopyButtonToElement(container: HTMLElement, getText: () => string) {
  const btn = createCopyButton(getText)
  container.appendChild(btn)
}

export interface ButtonGroupAction {
  icon: 'copy' | 'download'
  title: string
  onClick: () => void | Promise<void>
}

export function createButtonGroup(actions: ButtonGroupAction[]): HTMLDivElement {
  const group = document.createElement('div')
  group.className = 'copy-btn-group'

  for (const action of actions) {
    const btn = document.createElement('button')
    btn.className = 'copy-btn'
    btn.innerHTML = action.icon === 'copy' ? COPY_ICON : DOWNLOAD_ICON
    btn.title = action.title

    btn.addEventListener('click', async () => {
      try {
        await action.onClick()
        btn.innerHTML = CHECK_ICON
        btn.classList.add('copied')
        setTimeout(() => {
          btn.innerHTML = action.icon === 'copy' ? COPY_ICON : DOWNLOAD_ICON
          btn.classList.remove('copied')
        }, 2000)
      } catch {
        // Action failed
      }
    })

    group.appendChild(btn)
  }

  return group
}
